import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import useNotificationPermission from '../../hooks/useNotificationPermission';
import NotificationPromptModal from '../../components/NotificationPromptModal';
import { registerFcmToken } from '../../utils/fcm';
import ChatHistory from './components/ChatHistory';
import ConversationWindow from './components/ConversationWindow';
import AccountView from './components/AccountView';
import BottomNav from './components/BottomNav';
import {
  saveConversation,
  getConversation,
  getGuestConversations,
  saveMessage,
  incrementUnreadCount,
  resetUnreadCount,
  getUnreadCountByType,
  getGuestUnreadCount,
} from '../../utils/db';
import './Dashboard.css';

// Initialize the socket outside the component to avoid recreating it on every render.
// Socket.IO goes through Nginx reverse proxy on port 443 (WSS via HTTPS).
// Nginx routes /socket.io/* to the Rust backend on port 8084.
const serverUrl = process.env.REACT_APP_SOCKET_URL || `https://${window.location.hostname || '192.168.0.102'}`;
const socket = io(serverUrl, {
  autoConnect: false,
});

function Dashboard() {
  const { currentUser } = useAuth();
  const { addNotification } = useNotification();

  // When notification permission is granted (either immediately if already
  // granted, or after the user clicks "Enable Notifications" in the modal),
  // generate the FCM token and register it with the backend, mapped to the
  // user's Firebase UID.
  const handleNotificationGranted = useCallback(async () => {
    if (!currentUser) return;
    try {
      await registerFcmToken(currentUser.uid);
      console.log('📱 FCM token registered for user:', currentUser.uid);
    } catch (err) {
      console.warn('📱 FCM registration failed:', err.message);
      // Show a user-facing notification so the user knows push notifications
      // may not work. This can happen if the service worker fails to activate
      // or if the browser blocks push subscriptions.
      addNotification(
        '🔔 Push notifications could not be set up. You may miss messages when the app is in the background.',
        'warning',
        8000
      );
    }
  }, [currentUser, addNotification]);

  const {
    showPrompt,
    isPrompting,
    dismissPrompt,
    requestPermission,
    phase,
    showModal,
  } = useNotificationPermission({
    autoRequest: true,
    onGranted: handleNotificationGranted,
  });
  // autoRequest: true — auto-shows the explanation modal on mount when
  // permission is not yet granted. The user still clicks "Enable Notifications"
  // to trigger the browser's native permission dialog.
  const [activeTab, setActiveTab] = useState('chats'); // 'chats' or 'account'
  const [chatSubTab, setChatSubTab] = useState('mine'); // 'mine' or 'anonymous'
  const [activeChat, setActiveChat] = useState(null); // null if no chat is open
  const [refreshKey, setRefreshKey] = useState(0); // used to trigger ChatHistory refresh
  const [guestConversations, setGuestConversations] = useState([]);
  const [guestLoading, setGuestLoading] = useState(false);
  const [myChatsUnread, setMyChatsUnread] = useState(0);
  const [anonymousUnread, setAnonymousUnread] = useState(0);

  // Use a ref to track the active chat so socket event handlers (which have a
  // stable closure) can always read the latest activeChat value without needing
  // it in the useEffect dependency array.
  const activeChatRef = useRef(activeChat);
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Handle guest_joined event - when someone joins via invite link
  const handleGuestJoined = useCallback(async (data) => {
    console.log('🎉 Guest joined:', data);
    
    // Join the conversation room so we receive real-time messages
    if (socket && socket.connected) {
      socket.emit('join_room', data.conversation_id);
    }

    // Create or update the conversation in IndexedDB so it appears in chat list
    const existingConv = await getConversation(data.conversation_id);
    if (!existingConv) {
      await saveConversation({
        id: data.conversation_id,
        name: data.participant_name,
        userId: currentUser?.uid || 'mock-user-123',
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(data.participant_name)}&background=1e293b&color=fff`,
        lastMessage: data.message || `${data.participant_name} joined the chat!`,
        updatedAt: Date.now(),
        inviteCode: data.conversation_id.replace('invite_', ''),
        isInvite: true,
      });
    }

    // Refresh the chat list
    setRefreshKey(prev => prev + 1);
  }, [currentUser]);

  useEffect(() => {
    // Automatically connect to socket when dashboard mounts
    socket.connect();

    // Log all incoming socket events for debugging
    socket.onAny((event, ...args) => {
      console.log(`[Socket Event] ${event}`, args.length > 0 ? args[0] : '');
    });

    socket.on('connect', () => {
      console.log('Connected to socket server');
      // Register the current user with the socket server
      if (currentUser) {
        socket.emit('register_user', currentUser.uid);
      }
      // Re-join the active conversation room if one is open
      // This handles the case where the socket reconnects with a new ID
      // and needs to re-join the room to receive messages
      const currentActiveChat = activeChatRef.current;
      if (currentActiveChat && currentActiveChat.id) {
        console.log(`🏠 Re-joining conversation room after reconnect: ${currentActiveChat.id}`);
        socket.emit('join_room', currentActiveChat.id);
      }
    });

    // Listen for guest_joined events
    socket.on('guest_joined', handleGuestJoined);

    // Look up the conversation name from IndexedDB to show the sender's name
    const getSenderName = async (conversationId) => {
      try {
        const conv = await getConversation(conversationId);
        if (conv) {
          return conv.name || 'Someone';
        }
      } catch (err) {
        console.error('Failed to get conversation name:', err);
      }
      return 'Someone';
    };

    // Listen for new messages when the chat window is NOT open.
    // Uses activeChatRef.current to always read the latest activeChat value
    // without needing it in the dependency array (which would cause socket
    // disconnect/reconnect).
    const handleNewMessageNotification = async (data) => {
      const conversationId = data.conversation_id;
      if (!conversationId) return;

      // ALWAYS save the message to IndexedDB so it persists even if the chat
      // is currently open. The ConversationWindow will pick it up on reconnect.
      try {
        await saveMessage({
          conversationId,
          text: data.message,
          sender: 'them',
          timestamp: data.timestamp || Date.now(),
        });
      } catch (err) {
        console.error('Failed to save background message:', err);
      }

      // Only show notification and refresh list if the chat is NOT currently open
      // for this conversation (avoid duplicate alerts for the active chat)
      const currentActiveChat = activeChatRef.current;
      if (currentActiveChat && currentActiveChat.id === conversationId) {
        console.log(`🔇 Skipping notification for active chat: ${conversationId}`);
        return;
      }

      // Increment unread count for this conversation
      try {
        await incrementUnreadCount(conversationId);
      } catch (err) {
        console.error('Failed to increment unread count:', err);
      }

      // Look up the sender name from the stored conversation
      const senderName = await getSenderName(conversationId);

      // Show popup notification
      addNotification(
        `💬 New message from ${senderName}: "${data.message.length > 60 ? data.message.substring(0, 60) + '...' : data.message}"`,
        'info',
        5000
      );

      // Refresh the chat list so the new message appears in the history
      setRefreshKey(prev => prev + 1);
    };

    const handlePendingMessageNotification = async (data) => {
      const conversationId = data.conversation_id || data.conversationId;
      if (!conversationId) return;

      const msgText = data.text || '';

      // ALWAYS save the message to IndexedDB so it persists even if the chat
      // is currently open. The ConversationWindow will pick it up on reconnect.
      try {
        await saveMessage({
          conversationId,
          text: msgText,
          sender: 'them',
          timestamp: data.created_at || Date.now(),
        });
      } catch (err) {
        console.error('Failed to save pending message:', err);
      }

      // Only show notification and refresh list if the chat is NOT currently open
      // for this conversation (avoid duplicate alerts for the active chat)
      const currentActiveChat = activeChatRef.current;
      if (currentActiveChat && currentActiveChat.id === conversationId) {
        console.log(`🔇 Skipping notification for active chat: ${conversationId}`);
        return;
      }

      // Increment unread count for this conversation
      try {
        await incrementUnreadCount(conversationId);
      } catch (err) {
        console.error('Failed to increment unread count:', err);
      }

      // Look up the sender name from the stored conversation
      const senderName = await getSenderName(conversationId);

      // Show popup notification
      addNotification(
        `💬 New message from ${senderName}: "${msgText.length > 60 ? msgText.substring(0, 60) + '...' : msgText}"`,
        'info',
        5000
      );

      setRefreshKey(prev => prev + 1);
    };

    socket.on('new_message', handleNewMessageNotification);
    socket.on('pending_message', handlePendingMessageNotification);

    return () => {
      socket.off('guest_joined', handleGuestJoined);
      socket.off('new_message', handleNewMessageNotification);
      socket.off('pending_message', handlePendingMessageNotification);
      socket.disconnect();
    };
  }, [currentUser, handleGuestJoined, addNotification]);
  // NOTE: activeChat intentionally NOT in dependencies to prevent socket
  // disconnect/reconnect when opening/closing a conversation.
  // Room joining is handled by ConversationWindow's own effect.

  // Load guest conversations for the anonymous tab
  const loadGuestConversations = useCallback(async () => {
    setGuestLoading(true);
    try {
      // Exclude conversations owned by the current user (their own invites)
      const chats = await getGuestConversations(currentUser?.uid);
      setGuestConversations(chats);
    } catch (err) {
      console.error('Failed to load guest conversations:', err);
    } finally {
      setGuestLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (chatSubTab === 'anonymous') {
      loadGuestConversations();
    }
  }, [chatSubTab, loadGuestConversations, refreshKey]);

  // Load unread counts
  const loadUnreadCounts = useCallback(async () => {
    try {
      const userId = currentUser?.uid || 'mock-user-123';
      const [myChats, anonymous] = await Promise.all([
        getUnreadCountByType(userId, 'my_chat'),
        getGuestUnreadCount(userId),
      ]);
      setMyChatsUnread(myChats);
      setAnonymousUnread(anonymous);
    } catch (err) {
      console.error('Failed to load unread counts:', err);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts, refreshKey]);

  const handleSelectChat = async (chat) => {
    // Reset unread count when opening a chat
    try {
      await resetUnreadCount(chat.id);
    } catch (err) {
      console.error('Failed to reset unread count:', err);
    }
    setActiveChat(chat);
  };

  const handleBackToChats = () => {
    setActiveChat(null);
  };

  // If a chat is active, we take over the whole screen (hiding bottom nav and other views)
  if (activeChat) {
    return (
      <div className="dashboard-container">
        <ConversationWindow
          chat={activeChat}
            onBack={handleBackToChats}
            socket={socket}
            currentUser={currentUser}
          />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Notification permission modal overlay */}
      <NotificationPromptModal
        show={showPrompt}
        isPrompting={isPrompting}
        onEnable={requestPermission}
        onDismiss={dismissPrompt}
        isDenied={phase === 'denied'}
      />

      {/* Floating notification bell — shown when permission is not yet granted */}
      {phase !== 'granted' && phase !== 'unsupported' && (
        <button
          onClick={showModal}
          style={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 28px rgba(99, 102, 241, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.4)';
          }}
          aria-label="Enable notifications"
          title={phase === 'denied' ? 'Notifications are blocked — click to learn more' : 'Enable notifications'}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>
      )}

      <div className="dashboard-content">
        {activeTab === 'chats' ? (
          <>
            {/* Sub-tab navigation for My Chats vs Anonymous */}
            <div style={{
              display: 'flex',
              padding: '12px 16px 0',
              gap: 8,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <button
                onClick={() => setChatSubTab('mine')}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  background: chatSubTab === 'mine'
                    ? 'rgba(139, 92, 246, 0.15)'
                    : 'transparent',
                  color: chatSubTab === 'mine' ? '#8b5cf6' : '#a0aab2',
                  fontSize: 14,
                  fontWeight: chatSubTab === 'mine' ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                }}
              >
                My Chats
                {myChatsUnread > 0 && (
                  <span style={{
                    marginLeft: 6,
                    background: '#ef4444',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                    {myChatsUnread > 99 ? '99+' : myChatsUnread}
                  </span>
                )}
              </button>
              <button
                onClick={() => setChatSubTab('anonymous')}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  background: chatSubTab === 'anonymous'
                    ? 'rgba(139, 92, 246, 0.15)'
                    : 'transparent',
                  color: chatSubTab === 'anonymous' ? '#8b5cf6' : '#a0aab2',
                  fontSize: 14,
                  fontWeight: chatSubTab === 'anonymous' ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                }}
              >
                Anonymous
                {anonymousUnread > 0 && (
                  <span style={{
                    marginLeft: 6,
                    background: '#ef4444',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                    {anonymousUnread > 99 ? '99+' : anonymousUnread}
                  </span>
                )}
              </button>
            </div>

            {chatSubTab === 'mine' ? (
              <ChatHistory
                onSelectChat={handleSelectChat}
                currentUser={currentUser}
                refreshKey={refreshKey}
              />
            ) : (
              <div className="chat-history-container">
                <div className="chat-history-header">
                  <h1>Anonymous Chats</h1>
                  <p>
                    {guestConversations.length > 0
                      ? `You have ${guestConversations.length} anonymous chat${guestConversations.length !== 1 ? 's' : ''} from invite links.`
                      : 'No anonymous chats yet. Join an invite link to start chatting!'}
                  </p>
                </div>

                {guestLoading ? (
                  <div style={{ textAlign: 'center', color: '#a0aab2', padding: 40 }}>
                    Loading...
                  </div>
                ) : (
                  <div className="chat-list">
                    {guestConversations.map(chat => (
                      <div key={chat.id} className="chat-item" onClick={() => handleSelectChat(chat)}>
                        <img src={chat.avatar} alt={chat.name} className="chat-avatar" />
                        <div className="chat-info">
                          <h3 className="chat-name">{chat.name}</h3>
                          <p style={{ fontSize: 12, color: '#8b5cf6', margin: '2px 0' }}>
                            You joined as: <strong>{chat.guestName || 'Guest'}</strong>
                          </p>
                          <p className="chat-last-message">
                            {chat.lastMessage || 'No messages yet'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <AccountView />
        )}
      </div>
      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} totalUnread={myChatsUnread + anonymousUnread} />
    </div>
  );
}

export default Dashboard;
